import React, { useEffect, useState } from 'react';
import { properties } from '../services/api';
import axios from 'axios';
import { useIsMobile } from '../hooks/useIsMobile';
import yardrayLogo from '../assets/yardray-logo.png';
import './Weather.css';

const API = process.env.REACT_APP_API_URL;

const ALERT_SEVERITY_COLORS = {
  Extreme:  { bg: '#b71c1c', border: '#7f0000', text: 'white' },
  Severe:   { bg: '#c62828', border: '#b71c1c', text: 'white' },
  Moderate: { bg: '#e65100', border: '#bf360c', text: 'white' },
  Minor:    { bg: '#f57f17', border: '#e65100', text: 'white' },
  Unknown:  { bg: '#555',    border: '#333',    text: 'white' },
};

function getAlertColors(severity) {
  return ALERT_SEVERITY_COLORS[severity] || ALERT_SEVERITY_COLORS.Unknown;
}

function getWeatherIcon(code) {
  if (code === 0) return '☀️';
  if (code <= 2) return '⛅';
  if (code <= 3) return '☁️';
  if (code <= 49) return '🌫️';
  if (code <= 59) return '🌦️';
  if (code <= 69) return '🌧️';
  if (code <= 79) return '🌨️';
  if (code <= 84) return '🌧️';
  if (code <= 99) return '⛈️';
  return '🌡️';
}

function getWeatherDesc(code) {
  if (code === 0) return 'Clear sky';
  if (code <= 2) return 'Partly cloudy';
  if (code <= 3) return 'Overcast';
  if (code <= 49) return 'Foggy';
  if (code <= 59) return 'Drizzle';
  if (code <= 69) return 'Rain';
  if (code <= 79) return 'Snow';
  if (code <= 84) return 'Rain showers';
  if (code <= 99) return 'Thunderstorm';
  return 'Unknown';
}

function getWindDir(deg) {
  const dirs = ['N','NE','E','SE','S','SW','W','NW'];
  return dirs[Math.round(deg / 45) % 8];
}

function formatTime(isoStr) {
  if (!isoStr) return '--';
  const d = new Date(isoStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatDayLabel(dateStr, i) {
  if (i === 0) return 'Today';
  if (i === 1) return 'Tomorrow';
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const weekdays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return `${weekdays[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}

function getMoonPhase() {
  const known = new Date('2024-01-11');
  const now = new Date();
  const diff = (now - known) / (1000 * 60 * 60 * 24);
  const cycle = 29.53;
  const phase = ((diff % cycle) + cycle) % cycle;
  if (phase < 1.85)  return { name: 'New Moon',        icon: '🌑' };
  if (phase < 7.38)  return { name: 'Waxing Crescent', icon: '🌒' };
  if (phase < 9.22)  return { name: 'First Quarter',   icon: '🌓' };
  if (phase < 14.77) return { name: 'Waxing Gibbous',  icon: '🌔' };
  if (phase < 16.61) return { name: 'Full Moon',       icon: '🌕' };
  if (phase < 22.15) return { name: 'Waning Gibbous',  icon: '🌖' };
  if (phase < 23.99) return { name: 'Last Quarter',    icon: '🌗' };
  return { name: 'Waning Crescent', icon: '🌘' };
}

function isFreezeWarning(minTemp) {
  return minTemp <= 2;
}

function DismissButton({ onDismiss }) {
  return (
    <button className="weather-dismiss-btn" onClick={onDismiss}>✕</button>
  );
}

function Weather() {
  const [allProperties, setAllProperties] = useState([]);
  const [property, setProperty] = useState(null);
  const [weather, setWeather] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('yw_dismissed_alerts') || '[]'); }
    catch { return []; }
  });
  const [dismissedFreeze, setDismissedFreeze] = useState(false);
  const [expandedAlerts, setExpandedAlerts] = useState({});
  const [expandedDays, setExpandedDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState(localStorage.getItem('gardenbuddy_units') || 'F');
  const isMobile = useIsMobile();

  const formatTemp = (c) => {
    const f = Math.round(c * 9 / 5 + 32);
    if (units === 'C') return `${Math.round(c)}°C`;
    return `${f}°F`;
  };

  const handleUnitsChange = (val) => {
    setUnits(val);
    localStorage.setItem('gardenbuddy_units', val);
  };

  const dismissAlert = (id) => {
    const updated = [...dismissedAlerts, id];
    setDismissedAlerts(updated);
    sessionStorage.setItem('yw_dismissed_alerts', JSON.stringify(updated));
  };

  const toggleExpanded = (id) => {
    setExpandedAlerts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleDay = (i) => {
    setExpandedDays(prev => ({ ...prev, [i]: !prev[i] }));
  };

  const loadWeather = async (prop) => {
    setLoading(true);
    setError(null);
    setWeather(null);
    setAlerts([]);
    setDismissedFreeze(false);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [wRes, alertsRes] = await Promise.all([
        axios.get(`${API}/weather/location`, {
          headers,
          params: { latitude: prop.latitude, longitude: prop.longitude }
        }),
        axios.get(`${API}/weather/alerts`, {
          headers,
          params: { latitude: prop.latitude, longitude: prop.longitude }
        }).catch(() => ({ data: { alerts: [] } }))
      ]);
      setWeather(wRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setLoading(false);
    } catch (err) {
      console.error('Weather load error:', err);
      setError('Failed to load weather data.');
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const propsRes = await properties.getAll();
        const props = propsRes.data.properties.filter(p => p.latitude && p.longitude);
        if (props.length === 0) {
          setError('No property with location found. Add an address to a property in Settings.');
          setLoading(false);
          return;
        }
        setAllProperties(props);
        const savedId = localStorage.getItem('gardenbuddy_selected_property');
        const savedProp = savedId && savedId !== 'all' ? props.find(p => String(p.id) === savedId) : null;
        const initProp = savedProp || props[0];
        setProperty(initProp);
        await loadWeather(initProp);
      } catch (err) {
        console.error('Properties load error:', err);
        setError('Failed to load properties.');
        setLoading(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePropertyChange = async (e) => {
    const selected = allProperties.find(p => String(p.id) === e.target.value);
    if (selected) {
      setProperty(selected);
      localStorage.setItem('gardenbuddy_selected_property', selected.id);
      await loadWeather(selected);
    }
  };

  if (loading) return <div className="weather-loading">Loading weather...</div>;
  if (error) {
    const isNoProperty = error.includes('No property');
    return (
      <div className="weather-error-wrap">
        <img src={yardrayLogo} alt="Yardray" className="weather-error-logo" />
        <p className="weather-error-msg">{error}</p>
        {isNoProperty && (
          <button className="weather-error-btn" onClick={() => window.location.href = '/properties'}>
            Add Property
          </button>
        )}
      </div>
    );
  }
  if (!weather || !weather.daily || !weather.current || !weather.daily.weather_code) {
    return <div className="weather-unavailable">Weather data unavailable.</div>;
  }

  const cur = weather.current;
  const daily = weather.daily;
  const moon = getMoonPhase();
  const freezeWarnings = daily.time.slice(0, 3).filter((_, i) => isFreezeWarning(daily.temperature_2m_min[i]));
  const visibleAlerts = alerts.filter(a => !dismissedAlerts.includes(a.id));
  const showFreeze = freezeWarnings.length > 0 && !dismissedFreeze;

  const forecastGridCols = isMobile ? '90px 28px 1fr 70px' : '110px 32px 1fr 80px';

  return (
    <div className="weather-container">

      {visibleAlerts.map(alert => {
        const colors = getAlertColors(alert.severity);
        const isExpanded = expandedAlerts[alert.id];
        return (
          <div
            key={alert.id}
            className="weather-alert-card"
            style={{ backgroundColor: colors.bg, border: `2px solid ${colors.border}`, color: colors.text }}
          >
            <div className="weather-alert-inner">
              <div className="weather-alert-body" onClick={() => toggleExpanded(alert.id)}>
                <div className="weather-alert-title">
                  {alert.severity === 'Extreme' || alert.severity === 'Severe' ? '🚨 ' : '⚠️ '}
                  {alert.event}
                </div>
                <div className="weather-alert-headline">{alert.headline}</div>
                {isExpanded && alert.description && (
                  <div className="weather-alert-description">{alert.description}</div>
                )}
                {isExpanded && alert.instruction && (
                  <div className="weather-alert-instruction">{alert.instruction}</div>
                )}
                <div className="weather-alert-footer">
                  {isExpanded ? 'Tap to collapse' : 'Tap for details'}
                  {alert.expires && ` · Expires ${new Date(alert.expires).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                </div>
              </div>
              <DismissButton onDismiss={() => dismissAlert(alert.id)} />
            </div>
          </div>
        );
      })}

      {showFreeze && (
        <div className="weather-freeze-card">
          <div className="weather-freeze-inner">
            <div>
              <div className="weather-freeze-title">Freeze Warning</div>
              <div className="weather-freeze-text">
                Temperatures near or below freezing expected in the next {freezeWarnings.length === 1 ? 'day' : '2 days'}. Protect frost-sensitive plants.
              </div>
            </div>
            <DismissButton onDismiss={() => setDismissedFreeze(true)} />
          </div>
        </div>
      )}

      <div className="weather-header">
        <div className="weather-header-left">
          <h2>Weather</h2>
          {allProperties.length > 1 ? (
            <select className="weather-prop-select" value={property?.id} onChange={handlePropertyChange}>
              {allProperties.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          ) : (
            <div className="weather-prop-name">{property?.name} · {property?.address}</div>
          )}
        </div>
        <div className="weather-units-toggle">
          {['C', 'F'].map(u => (
            <button
              key={u}
              className={`weather-unit-btn${units === u ? ' weather-unit-btn--active' : ''}`}
              onClick={() => handleUnitsChange(u)}
            >
              {u}°
            </button>
          ))}
        </div>
      </div>

      <div className="weather-current-card">
        <div className="weather-current-top">
          <div>
            <div className="weather-current-icon">{getWeatherIcon(cur.weather_code)}</div>
            <div className="weather-current-desc">{getWeatherDesc(cur.weather_code)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="weather-current-temp">{formatTemp(cur.temperature_2m)}</div>
            <div className="weather-current-feels">Feels like {formatTemp(cur.apparent_temperature)}</div>
          </div>
        </div>
        <div className="weather-current-grid">
          <div className="weather-current-cell">
            <div className="weather-current-cell-icon">💧</div>
            <div className="weather-current-cell-value">{cur.relative_humidity_2m}%</div>
            <div className="weather-current-cell-label">Humidity</div>
          </div>
          <div className="weather-current-cell">
            <div className="weather-current-cell-icon">🌧️</div>
            <div className="weather-current-cell-value">{cur.precipitation} mm</div>
            <div className="weather-current-cell-label">Precipitation</div>
          </div>
          <div className="weather-current-cell">
            <div className="weather-current-cell-icon">💨</div>
            <div className="weather-current-cell-value">{Math.round(cur.wind_speed_10m)} km/h</div>
            <div className="weather-current-cell-label">Wind {getWindDir(cur.wind_direction_10m)}</div>
          </div>
          <div className="weather-current-cell">
            <div className="weather-current-cell-icon">{moon.icon}</div>
            <div className="weather-current-cell-value">{moon.name}</div>
            <div className="weather-current-cell-label">Moon</div>
          </div>
        </div>
      </div>

      <div className="weather-notes-card">
        <h3>Garden Notes</h3>
        <div className="weather-notes-list">
          {cur.precipitation > 2 && <div>• Rain today — skip manual watering</div>}
          {daily.precipitation_sum?.[0] === 0 && cur.temperature_2m > 20 && <div>• No rain today and warm — check soil moisture</div>}
          {daily.uv_index_max?.[0] >= 8 && <div>• High UV today — avoid transplanting in full sun</div>}
          {freezeWarnings.length > 0 && <div>• Frost risk — bring in tender plants or cover overnight</div>}
          {daily.wind_speed_10m_max?.[0] > 40 && <div>• Strong winds expected — stake tall plants</div>}
          {cur.relative_humidity_2m > 85 && <div>• High humidity — watch for fungal issues</div>}
          {visibleAlerts.length > 0 && <div>• Active weather alerts in effect — check alert details above</div>}
          {cur.precipitation === 0 && !isFreezeWarning(daily.temperature_2m_min[0]) && daily.uv_index_max?.[0] < 8 && (daily.wind_speed_10m_max?.[0] || 0) <= 40 && cur.relative_humidity_2m <= 85 && visibleAlerts.length === 0 && (
            <div>• No alerts or concerns today — good day to get outside.</div>
          )}
        </div>
      </div>

      <div className="weather-sun-grid">
        <div className="weather-sunrise-card">
          <div className="weather-sunrise-time">{formatTime(daily.sunrise?.[0])}</div>
          <div className="weather-sunrise-label">Sunrise</div>
        </div>
        <div className="weather-sunset-card">
          <div className="weather-sunset-time">{formatTime(daily.sunset?.[0])}</div>
          <div className="weather-sunset-label">Sunset</div>
        </div>
      </div>

      <div className="weather-forecast-card">
        <h3>7-Day Forecast</h3>

        <div className="weather-forecast-header" style={{ gridTemplateColumns: forecastGridCols }}>
          <div className="weather-forecast-header-label"></div>
          <div className="weather-forecast-header-label"></div>
          <div className="weather-forecast-header-label"></div>
          <div className="weather-forecast-header-temps">
            <span className="weather-forecast-header-temp">High</span>
            <span className="weather-forecast-header-temp">Low</span>
          </div>
        </div>

        <div className="weather-forecast-list">
          {daily.time.map((dateStr, i) => {
            const isExpDay = expandedDays[i];
            const hasFreezeToday = isFreezeWarning(daily.temperature_2m_min[i]);
            return (
              <div key={dateStr}>
                <div
                  className={`weather-forecast-row${i === 0 ? ' weather-forecast-row--today' : ''}`}
                  style={{ gridTemplateColumns: forecastGridCols }}
                  onClick={() => toggleDay(i)}
                >
                  <div className={`weather-forecast-day${i === 0 ? ' weather-forecast-day--today' : ''}`}>
                    {formatDayLabel(dateStr, i)}
                  </div>
                  <div className="weather-forecast-icon">{getWeatherIcon(daily.weather_code[i])}</div>
                  <div>
                    <div className="weather-forecast-desc">
                      {getWeatherDesc(daily.weather_code[i])}
                      {daily.precipitation_probability_max[i] > 20 && (
                        <span className="weather-forecast-rain">
                          💧 {daily.precipitation_probability_max[i]}%
                        </span>
                      )}
                    </div>
                    {hasFreezeToday && (
                      <div className="weather-forecast-freeze">Freeze risk</div>
                    )}
                  </div>
                  <div className="weather-forecast-temps">
                    <span className="weather-forecast-high">{formatTemp(daily.temperature_2m_max[i])}</span>
                    <span className="weather-forecast-low">{formatTemp(daily.temperature_2m_min[i])}</span>
                  </div>
                </div>

                {isExpDay && (
                  <div className="weather-forecast-detail">
                    <div>UV Index: {daily.uv_index_max?.[i] ?? '--'}</div>
                    <div>Rain chance: {daily.precipitation_probability_max[i]}%</div>
                    <div>Rain total: {daily.precipitation_sum?.[i] ?? 0} mm</div>
                    <div>Sunrise: {formatTime(daily.sunrise?.[i])}</div>
                    <div>Sunset: {formatTime(daily.sunset?.[i])}</div>
                    {hasFreezeToday && (
                      <div className="weather-forecast-freeze-warn">Freeze risk — protect sensitive plants</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}

export default Weather;
